import { type ReactNode, useRef } from 'react';

import Carousel, { type CarouselRef } from 'components/Carousel';
import IconButton from 'components/IconButton';
import ModalDialog, {
	MODAL_DIALOG_TOUCH_MOVE_WHITELIST_CLASS,
} from 'components/ModalDialog';
import ThumbnailList, {
	type ThumbnailListImage,
} from 'components/ThumbnailList';
import { useDebounce } from 'hooks';
import { cn } from 'utils/classNames';
import { afterNextPaint, empty } from 'utils/helpers';
import { useI18n } from 'utils/i18n';
import { clampIndex } from 'utils/math';
import { ENTITIES } from 'utils/string';

interface Props {
	hasControls?: boolean;
	hasCustomThumbnailImageDimensions?: boolean;
	/** Hide image count, thumbnails and lock carousel to the current image. */
	hasSingleImageFocus?: boolean;
	id: string;
	imageCountTheme?: 'dark' | 'light';
	isOpen: boolean;
	items: ReactNode[];
	label: string;
	onCarouselNext?: () => void;
	onCarouselPrevious?: () => void;
	onCarouselScrollEnd?: (index: number) => void;
	onClose: () => void;
	selectedImageIndex: number;
	setSelectedImageIndex: (index: number) => void;
	thumbnailImages?: ThumbnailListImage[];
}

/**
 * Dialog with carousel and thumbnails for product images, product videos
 * and customer review images.
 */
export default function MediaDialog({
	hasControls = true,
	hasCustomThumbnailImageDimensions,
	hasSingleImageFocus = false,
	id,
	imageCountTheme = 'light',
	isOpen,
	items,
	label,
	onCarouselNext,
	onCarouselPrevious,
	onCarouselScrollEnd,
	onClose,
	selectedImageIndex,
	setSelectedImageIndex,
	thumbnailImages = empty.array,
}: Props) {
	const { t } = useI18n();
	const imageCarouselRef = useRef<CarouselRef>(null);
	const thumbnailListRef = useRef<CarouselRef>(null);

	const imageCount = items.length;

	const transitionDuration = 300;
	const transitionDurationClass = 'duration-300';
	const isOpenDebounced = useDebounce(isOpen, transitionDuration);
	// Opened and transitioned in. Also check isOpen to get false immediately
	// when closing.
	const isOpenFullyOpen = isOpenDebounced && isOpen;

	const updateSelectedImageIndex = (index: number) => {
		setSelectedImageIndex(clampIndex(index, imageCount));
	};

	return (
		<ModalDialog
			id={id}
			isOpen={isOpen}
			onClose={onClose}
			onOpen={() => {
				// Wait for a possibly initial render to ensure refs are set.
				// This is in addition to the initialIndex prop on the Carousel
				// since the dialog can be opened and closed while the Carousel
				// remains mounted.
				afterNextPaint(() => {
					thumbnailListRef.current?.goToIndex(selectedImageIndex, {
						animate: false,
					});
					imageCarouselRef.current?.goToIndex(selectedImageIndex, {
						animate: false,
						moveFocusOnDone: true,
					});
				});
			}}
			ariaLabel={label}
			className={cn(
				'h-full w-full overflow-hidden bg-base',
				'md:m-auto md:h-[calc(100%-var(--r-space-md)*2)] md:w-[calc(100%-var(--r-space-md)*2)] md:rounded-md',
				'forced-colors:border',
				'transition-[opacity,translate] transition-discrete',
				transitionDurationClass,
				'translate-y-0 opacity-100 starting:translate-y-[10%] starting:opacity-0',
			)}
		>
			<p
				className={cn(
					'absolute top-0 left-1/2 z-2 mt-r-md -translate-x-1/2',
					'rounded-md px-2 py-1',
					imageCountTheme === 'dark'
						? 'bg-positive/80 text-negative'
						: 'bg-base text-base',
				)}
				hidden={hasSingleImageFocus}
			>
				<span className="sr-only">
					{t('general_image_of_total_text', {
						number: selectedImageIndex + 1,
						total: imageCount,
					})}
				</span>
				<span aria-hidden="true">
					{`${selectedImageIndex + 1}${ENTITIES.narrowNoBreakSpace}/${ENTITIES.narrowNoBreakSpace}${imageCount}`}
				</span>
			</p>
			<IconButton
				onClick={onClose}
				icon="close"
				variant="secondary"
				text={t('general_close_dialog_button_label')}
				className="absolute top-r-md right-r-md z-3"
			/>

			<div className="grid h-full grid-cols-1 grid-rows-[1fr_min-content]">
				<Carousel
					ref={imageCarouselRef}
					items={items}
					controlsLayout="inline-floating"
					controlsSize="md"
					alignment="center"
					gutter="none"
					hasControls={hasControls}
					hasProgressTracker={false}
					disabled={hasSingleImageFocus || !isOpen}
					initialIndex={selectedImageIndex}
					moveFocusOnInitialIndex
					className="relative max-h-full min-h-0"
					carouselClassName={cn(
						'flex',
						MODAL_DIALOG_TOUCH_MOVE_WHITELIST_CLASS,
						!hasSingleImageFocus && 'h-full',
					)}
					itemClassName="relative flex max-h-full w-full items-center justify-center"
					nextButtonLabel={t('general_carousel_next_image_button_label')}
					prevButtonLabel={t('general_carousel_previous_image_button_label')}
					prevButtonClassName="left-r-md"
					nextButtonClassName="right-r-md"
					onNext={() => {
						updateSelectedImageIndex(selectedImageIndex + 1);
						onCarouselNext?.();
					}}
					onPrevious={() => {
						updateSelectedImageIndex(selectedImageIndex - 1);
						onCarouselPrevious?.();
					}}
					onScrollEnd={(carouselItems) => {
						// Moving index on open should not trigger this.
						if (!isOpenFullyOpen) {
							return;
						}
						// Using isFullyVisible should be enough, but it's a bit flaky.
						const index = carouselItems.findIndex(
							(item) => (item.visiblePercentage ?? 0) > 75,
						);
						if (index !== -1) {
							updateSelectedImageIndex(index);
							onCarouselScrollEnd?.(index);
						}
					}}
				/>

				{thumbnailImages.length > 1 && (
					<div
						className="flex justify-center border-t border-t-subtle p-r-md"
						hidden={hasSingleImageFocus}
					>
						<ThumbnailList
							ref={thumbnailListRef}
							size="md"
							images={thumbnailImages}
							hasCustomImageDimensions={hasCustomThumbnailImageDimensions}
							carouselClassName={MODAL_DIALOG_TOUCH_MOVE_WHITELIST_CLASS}
							selectedImageIndex={selectedImageIndex}
							isScreenReaderHidden
							onThumbnailClick={(i) => {
								updateSelectedImageIndex(i);
								imageCarouselRef.current?.goToIndex(i, { animate: false });
							}}
						/>
					</div>
				)}
			</div>
		</ModalDialog>
	);
}
MediaDialog.displayName = 'MediaDialog';
